/**
 * Sparkline — dependency-free inline SVG trend line (v0.5.8 glassboard kit).
 *
 * Honest by construction: renders nothing (null) with fewer than two finite
 * datapoints — never a faked flat line. Color defaults to trend direction
 * (last vs first) using the semantic tokens via currentColor.
 */

export type SparklineTone = 'auto' | 'ok' | 'bad' | 'accent' | 'mute';

const TONE_CLASS: Record<Exclude<SparklineTone, 'auto'>, string> = {
  ok: 'text-ok',
  bad: 'text-bad',
  accent: 'text-accent',
  mute: 'text-ink-mute',
};

export interface SparklineProps {
  values: number[];
  width?: number;
  height?: number;
  strokeWidth?: number;
  tone?: SparklineTone;
  /** Soft area fill under the line. */
  area?: boolean;
  /** Dot on the most recent point. */
  dot?: boolean;
  className?: string;
  'aria-label'?: string;
}

export function Sparkline({
  values,
  width = 120,
  height = 32,
  strokeWidth = 1.5,
  tone = 'auto',
  area = false,
  dot = true,
  className = '',
  'aria-label': ariaLabel,
}: SparklineProps) {
  const finite = values.filter((v) => Number.isFinite(v));
  if (finite.length < 2) return null;

  const min = Math.min(...finite);
  const max = Math.max(...finite);
  const range = Math.max(1e-9, max - min);
  // Padding keeps the stroke and last-point dot inside the viewBox.
  const pad = Math.max(strokeWidth, dot ? 2.5 : strokeWidth);
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;

  const pts = finite.map((v, i) => ({
    x: pad + (i / (finite.length - 1)) * innerW,
    y: pad + innerH - ((v - min) / range) * innerH,
  }));

  const line = pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(' ');
  const areaPath = `${line} L ${pts[pts.length - 1].x.toFixed(2)} ${(height - pad).toFixed(2)} L ${pts[0].x.toFixed(2)} ${(height - pad).toFixed(2)} Z`;

  const rising = finite[finite.length - 1] >= finite[0];
  const toneClass = tone === 'auto' ? (rising ? 'text-ok' : 'text-bad') : TONE_CLASS[tone];
  const last = pts[pts.length - 1];

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      className={`${toneClass} ${className}`.trim()}
      role="img"
      aria-label={ariaLabel ?? `trend of ${finite.length} points, ${rising ? 'up' : 'down'}`}
      data-testid="sparkline"
      data-trend={rising ? 'up' : 'down'}
    >
      {area && <path d={areaPath} fill="currentColor" opacity={0.12} stroke="none" />}
      <path d={line} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinejoin="round" strokeLinecap="round" />
      {dot && <circle cx={last.x} cy={last.y} r={2} fill="currentColor" />}
    </svg>
  );
}
